// Explainer
// ---------
// The Layer-3 primitive of the contextual-help system: a collapsible panel
// that keeps a longer, worked explanation one click away without crowding
// the form. Generalises the original PathScopeExamples toggle so any
// surface with a genuinely subtle mechanic (scope rules, license model,
// verification steps) can offer the same "Show / Hide" affordance with
// consistent styling.
//
// Use for multi-sentence or multi-example content. For a one-line
// definition next to a label, use <HelpTip> instead.

import React, { useId, useState } from "react";
import { ChevronDown } from "lucide-react";

export interface ExplainerProps {
  // Toggle text; rendered as "Show {label}" / "Hide {label}".
  // e.g. label="examples" → "Show examples". Defaults to "how this works".
  label?: string;
  // Whether the panel starts open. Default closed — open it for mechanics
  // the user almost always needs to read before acting.
  defaultOpen?: boolean;
  // The explanation. Rendered inside the bordered panel when open.
  children: React.ReactNode;
  // Extra classes on the outer wrapper (spacing at the call site).
  className?: string;
}

export default function Explainer({
  label = "how this works",
  defaultOpen = false,
  children,
  className,
}: ExplainerProps) {
  const [open, setOpen] = useState(defaultOpen);
  const panelId = `explainer-${useId()}`;

  return (
    <div className={className ?? "mb-4"}>
      <button
        type="button"
        onClick={() => setOpen((previousOpen) => !previousOpen)}
        aria-expanded={open}
        aria-controls={panelId}
        className="inline-flex items-center gap-1 text-xs font-medium text-blue-600 hover:text-blue-700"
      >
        <ChevronDown
          className={`h-3.5 w-3.5 transition-transform ${open ? "rotate-180" : ""}`}
          aria-hidden="true"
        />
        {open ? "Hide" : "Show"} {label}
      </button>

      {open && (
        <div
          id={panelId}
          className="mt-3 rounded-lg border border-gray-200 bg-gray-50 p-4 text-xs leading-relaxed text-gray-600"
        >
          {children}
        </div>
      )}
    </div>
  );
}
